import * as React from 'react';
import { connect } from 'react-redux';

import classnames from 'classnames';

import lodashIsequal from 'lodash.isequal';

import {
  Button,
  Checkbox,
  createStyles,
  Divider,
  Drawer,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Theme,
  Typography,
  WithStyles,
  withStyles
} from '@material-ui/core';
import { ChevronLeftRounded, DoneRounded } from '@material-ui/icons';

import * as FilterActions from '@actions/FilterActions';
import { logEvent } from '@api/analytics';
import { HigherLowerNone, IFilterState, IMapFilters, IStore } from '@reducers';

import FilterSlider from './FilterSlider';

const mapDispatchToProps = {
  onFiltersChange: FilterActions.onFiltersChange
};

const drawerWidth = 350;

const styles = (theme: Theme) =>
  createStyles({
    drawer: {
      width: drawerWidth,
      flexShrink: 0
    },
    drawerPaper: {
      width: drawerWidth,
      overflowX: 'hidden'
    },
    drawerHeader: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(0, 1),
      ...theme.mixins.toolbar,
      justifyContent: 'flex-start'
    },
    toolbar: theme.mixins.toolbar,
    drawerContent: {
      marginTop: theme.spacing(1),
      marginLeft: theme.spacing(3),
      marginRight: theme.spacing(3),
      marginBottom: theme.spacing(1)
    },
    control: {
      width: '100%'
    },
    dropdown: {
      marginBottom: theme.spacing(1)
    }
  });

interface IProps {
  isOpen: boolean;
  onClose();
}

const MapFilters: React.FC<IFilterState &
  WithStyles<typeof styles> &
  typeof mapDispatchToProps &
  IProps> = ({ classes, isOpen, onClose, onFiltersChange, filters }) => {
  const [unsavedFilters, setUnsavedFilters] = React.useState<IMapFilters>(
    filters
  );

  const filtersReset = () => {
    logEvent({ category: 'Map', action: 'Filters Reset' });
    setUnsavedFilters(filters);
  };

  const clearAndClose = () => {
    logEvent({ category: 'Map', action: 'Filters Cleared and Closed' });
    setUnsavedFilters(filters);
    onClose();
  };

  const saveAndClose = () => {
    logEvent({ category: 'Map', action: 'Filters Changed' });
    onFiltersChange(unsavedFilters);
    onClose();
  };

  const onUnsavedFiltersSelectChange = (key: string) => (
    e: React.ChangeEvent<{ value: HigherLowerNone }>
  ) => {
    setUnsavedFilters({
      ...unsavedFilters,
      [key]: e.target.value
    });
  };

  const onUnsavedFiltersBooleanChange = (
    key: string,
    trueValue: HigherLowerNone
  ) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setUnsavedFilters({
      ...unsavedFilters,
      [key]: e.target.checked ? trueValue : HigherLowerNone.NoPref
    });
  };

  return (
    <Drawer
      className={classes.drawer}
      open={isOpen}
      onClose={clearAndClose}
      classes={{
        paper: classes.drawerPaper
      }}
    >
      <div className={classes.drawerHeader}>
        <IconButton onClick={clearAndClose}>
          <ChevronLeftRounded />
        </IconButton>
        <Typography>Map Filters</Typography>
      </div>

      <Divider />

      <Typography variant="subtitle1" className={classes.drawerContent}>
        Market Profile
      </Typography>

      <div className={classes.drawerContent}>
        <Typography variant="caption">Average Age</Typography>

        <FilterSlider
          storeKey="preferCrowdAge"
          unsavedFilters={unsavedFilters}
          setUnsavedFilters={setUnsavedFilters}
        />

        <Typography variant="caption">Average Income</Typography>

        <FilterSlider
          variant="dollars"
          storeKey="preferIncome"
          unsavedFilters={unsavedFilters}
          setUnsavedFilters={setUnsavedFilters}
        />

        <Typography variant="caption">Education</Typography>

        <Select
          className={classnames(classes.control, classes.dropdown)}
          id="education-select"
          value={unsavedFilters.preferEducation}
          onChange={onUnsavedFiltersSelectChange('preferEducation')}
        >
          <MenuItem value={HigherLowerNone.NoPref}>No Preference</MenuItem>
          <MenuItem value={HigherLowerNone.Higher}>
            Prefer Higher Education
          </MenuItem>
          <MenuItem value={HigherLowerNone.Lower}>
            Prefer Lower Education
          </MenuItem>
        </Select>
      </div>

      <Divider variant="middle" />

      <Typography variant="subtitle1" className={classes.drawerContent}>
        Housing
      </Typography>

      <div className={classes.drawerContent}>
        <Typography variant="caption">Home Price</Typography>

        <FilterSlider
          variant="dollars"
          storeKey="preferHomePrice"
          unsavedFilters={unsavedFilters}
          setUnsavedFilters={setUnsavedFilters}
        />

        <Typography variant="caption">Rent Price</Typography>

        <FilterSlider
          variant="dollars"
          storeKey="preferRentPrice"
          unsavedFilters={unsavedFilters}
          setUnsavedFilters={setUnsavedFilters}
        />
      </div>

      <Divider variant="middle" />

      <Typography variant="subtitle1" className={classes.drawerContent}>
        Surrounding Neighborhood
      </Typography>

      <div className={classes.drawerContent}>
        <FormControlLabel
          control={
            <Checkbox
              checked={
                unsavedFilters.preferHigherAirbnbAbility ===
                HigherLowerNone.Higher
              }
              onChange={onUnsavedFiltersBooleanChange(
                'preferHigherAirbnbAbility',
                HigherLowerNone.Higher
              )}
              value="higherAirbnb"
            />
          }
          label="Prefer higher Airbnb-ability"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={
                unsavedFilters.preferSchoolRatings === HigherLowerNone.Higher
              }
              onChange={onUnsavedFiltersBooleanChange(
                'preferSchoolRatings',
                HigherLowerNone.Higher
              )}
              value="higherRatedSchools"
            />
          }
          label="Prefer higher rated schools"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={unsavedFilters.preferCrimeRate === HigherLowerNone.Lower}
              onChange={onUnsavedFiltersBooleanChange(
                'preferCrimeRate',
                HigherLowerNone.Lower
              )}
              value="lowerCrime"
            />
          }
          label="Prefer lower crime rates"
        />
      </div>

      <Divider variant="middle" />

      <Grid container spacing={0} className={classes.drawerContent}>
        <Grid item xs={5} justify="flex-start">
          <Button
            disabled={lodashIsequal(unsavedFilters, filters)}
            size="medium"
            variant="contained"
            onClick={filtersReset}
            aria-label="Reset Filters"
          >
            Reset
          </Button>
        </Grid>
        <Grid item xs={7} justify="flex-end">
          <Button
            size="medium"
            variant="contained"
            color="primary"
            startIcon={<DoneRounded />}
            onClick={saveAndClose}
            aria-label="Save Filters"
          >
            Apply Filters
          </Button>
        </Grid>
      </Grid>
    </Drawer>
  );
};

export default withStyles(styles)(
  connect(
    (state: IStore): IFilterState => state.filter,
    mapDispatchToProps
  )(MapFilters)
);
